import { useMemo } from 'react';
import { Moon, Star, BookOpen } from 'lucide-react';

const RAMADAN_PROGRESS_TEXT = {
  de: { title: 'Deine Ramadan-Reise', dayOf: 'Tag', of: 'von', completed: 'erledigt', blessedNights: 'Gesegnete Nächte', quranJourney: 'Quran-Reise' },
  en: { title: 'Your Ramadan Journey', dayOf: 'Day', of: 'of', completed: 'completed', blessedNights: 'Blessed Nights', quranJourney: 'Quran Journey' },
  bs: { title: 'Tvoj Ramazanski Put', dayOf: 'Dan', of: 'od', completed: 'završeno', blessedNights: 'Blagoslovljene Noći', quranJourney: 'Kur\'anski Put' },
  ar: { title: 'رحلتك في رمضان', dayOf: 'يوم', of: 'من', completed: 'مكتمل', blessedNights: 'الليالي المباركة', quranJourney: 'رحلة القرآن' },
  id: { title: 'Perjalanan Ramadanmu', dayOf: 'Hari', of: 'dari', completed: 'selesai', blessedNights: 'Malam Berkah', quranJourney: 'Perjalanan Al-Quran' },
  tr: { title: 'Ramazan Yolculuğun', dayOf: 'Gün', of: '/', completed: 'tamamlandı', blessedNights: 'Mübarek Geceler', quranJourney: 'Kur\'an Yolculuğu' },
};

const RamadanProgress = ({ ramadanDay, completedDates, language = 'de' }) => {
  const t = RAMADAN_PROGRESS_TEXT[language] || RAMADAN_PROGRESS_TEXT.de;
  
  // Berechne erledigte Ramadan-Tage aus completedDates
  const ramadanCompletedDays = useMemo(() => {
    const completed = new Set();
    // Ramadan Tag 1 = 2026-02-19, Tag 2 = 2026-02-20, etc.
    completedDates.forEach(dateStr => {
      const date = new Date(dateStr);
      const feb19 = new Date(2026, 1, 19);
      feb19.setHours(0, 0, 0, 0);
      date.setHours(0, 0, 0, 0);
      const diff = Math.floor((date - feb19) / (1000 * 60 * 60 * 24)) + 1;
      if (diff >= 1 && diff <= 30) {
        completed.add(diff);
      }
    });
    return completed;
  }, [completedDates]);

  const completedCount = ramadanCompletedDays.size;
  const progressPercent = (completedCount / 30) * 100;
  
  // 30 Tage in 5 Reihen × 6 Spalten
  const days = Array.from({ length: 30 }, (_, i) => {
    const day = i + 1;
    return {
      day,
      isCompleted: ramadanCompletedDays.has(day),
      isCurrent: day === ramadanDay,
      isFuture: day > ramadanDay,
      isBlessedNight: day >= 21 && day % 2 === 1,
    };
  });

  return (
    <div className="ramadan-progress" data-testid="ramadan-progress">
      <div className="ramadan-progress-header">
        <Moon size={14} className="rp-moon-icon" />
        <span className="rp-title">{t.title}</span>
      </div>
      
      {/* Fortschrittsbalken */}
      <div className="rp-bar-container" data-testid="ramadan-progress-bar">
        <div className="rp-bar-track">
          <div 
            className="rp-bar-fill"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
        <span className="rp-bar-label">{completedCount}/{30}</span>
      </div>

      {/* 30-Tage-Grid */}
      <div className="rp-grid" data-testid="ramadan-day-grid">
        {days.map(d => (
          <div
            key={d.day}
            className={`rp-day${d.isCompleted ? ' completed' : ''}${d.isCurrent ? ' current' : ''}${d.isFuture ? ' future' : ''}${d.isBlessedNight ? ' blessed' : ''}`}
            data-testid={`ramadan-day-${d.day}`}
          >
            {d.isCompleted ? (
              <Star size={10} className="rp-day-star" />
            ) : d.isBlessedNight && !d.isFuture ? (
              <Moon size={9} className="rp-day-moon" />
            ) : (
              <span className="rp-day-num">{d.day}</span>
            )}
          </div>
        ))}
      </div>

      {/* Quran-Reise Indikator */}
      <div className="rp-quran-row">
        <BookOpen size={12} />
        <span>{t.quranJourney}: {t.dayOf} {ramadanDay} {t.of} 30</span>
      </div>
    </div>
  );
};

export default RamadanProgress;
